import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerText} from '../../../../../libs/shared/ui/src/components/banners/bannerText';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  ConversationStatusesEnum,
  findConversationsReminder,
  findConversationStatus,
} from '../../../../helpers/models/conversationHelpers';
import {ConversationBaseModel} from '../../../../models/api/conversationModel';
import {TagModel} from '../../../../models/api/tagModel';
import {
  ConversationUndoBannerState,
  ConversationUndoBannerTypesEnum,
} from '../../../../store/panels/app/banners/bannersState';
import {placeholderSubjectsByMessageType} from '../../../inboxes/cells/content/cell/cellMiddle';
import {ConversationSubjectContainer} from '../../../inboxes/viewer/conversation/header/conversationSubjectContainer';

/*
 * Props.
 */

type ConversationUndoBannerTagProps = {
  added: {
    tag: TagModel | undefined;
    isStarredTag: boolean;
  };
  removed: {
    tag: TagModel | undefined;
    isStarredTag: boolean;
  };
};
type ConversationUndoBannerInboxProps = {
  inbox: InboxModel;
};

interface ConversationUndoBannerProps {
  state: ConversationUndoBannerState;
  tagProps: ConversationUndoBannerTagProps | undefined;
  inboxProps: ConversationUndoBannerInboxProps | undefined;
  onUndoClick: () => void;
  onViewClick: () => void;
}

/*
 * Styles.
 */

const StyledSecondarySpan = styled.span`
  margin-left: 5px;
  color: ${(p) => p.theme.bannerSecondaryText};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

export const ConversationUndoBanner: FC<ConversationUndoBannerProps> = (props) => {
  const content = buildContent(props);
  if (!content) {
    return null;
  }

  const shouldRenderUndoButton = props.state.conversationUndoType !== ConversationUndoBannerTypesEnum.MOVE;
  const shouldRenderViewButton =
    props.state.conversationUndoType === ConversationUndoBannerTypesEnum.MOVE &&
    props.state.newConversations.length === 1;
  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.TRANSIENT}>
      <BannerIcon iconName={content.icon} />
      <BannerText>
        {content.text}
        {maybeRenderConversationSubject(props)}
      </BannerText>
      {shouldRenderUndoButton && (
        <BannerAction onClick={props.onUndoClick}>
          <Trans id="banner-undo">Undo</Trans>
        </BannerAction>
      )}
      {shouldRenderViewButton && (
        <BannerAction onClick={props.onViewClick}>
          <Trans id="banner-view">View</Trans>
        </BannerAction>
      )}
    </Banner>
  );
};

/*
 * Content.
 */

interface UndoBannerContent {
  icon: IconName;
  text: ReactNode;
}

function buildContent(props: ConversationUndoBannerProps) {
  // Find the conversation to use as reference for the action.
  const {state, tagProps, inboxProps} = props;

  const conversationCount = state.newConversations.length;
  const newConversation = state.newConversations[0];
  if (!newConversation) {
    return null;
  }

  switch (props.state.conversationUndoType) {
    case ConversationUndoBannerTypesEnum.ASSIGNEE:
      return buildAssigneeContent(newConversation, conversationCount);

    case ConversationUndoBannerTypesEnum.REMINDER:
      return buildReminderContent(state, conversationCount);

    case ConversationUndoBannerTypesEnum.STAGE:
      return buildStageContent(conversationCount);

    case ConversationUndoBannerTypesEnum.STATUS:
      return buildStatusContent(props, newConversation, conversationCount);

    case ConversationUndoBannerTypesEnum.MOVE:
      return buildMoveContent(inboxProps?.inbox, conversationCount);

    case ConversationUndoBannerTypesEnum.TAG:
    default:
      return buildTagContent(tagProps, conversationCount);
  }
}

function buildMoveContent(
  inbox: InboxModel | undefined,
  conversationCount: number,
): UndoBannerContent | null {
  if (!inbox) {
    return null;
  }

  return {
    icon: 'moveRightFilledSmall',
    text: (
      <Plural
        id="banner-moved"
        value={conversationCount}
        one={`Moved to "${inbox.name}"`}
        other={`Moved # to "${inbox.name}"`}
      />
    ),
  };
}

function buildAssigneeContent(
  conversation: ConversationBaseModel,
  conversationCount: number,
): UndoBannerContent {
  // Case #1: this was assigned.
  if (conversation.assigneeId) {
    return {
      icon: 'unassignFilledSmall',
      text: <Plural id="banner-assigned" value={conversationCount} one="Assigned" other="# assigned" />,
    };
  }

  // Otherwise, it was unassigned.
  return {
    icon: 'unassignFilledSmall',
    text: <Plural id="banner-unassigned" value={conversationCount} one="Unassigned" other="# unassigned" />,
  };
}

function buildReminderContent(
  state: ConversationUndoBannerState,
  conversationCount: number,
): UndoBannerContent {
  const remindAt = findConversationsReminder(state.cellsContext, state.newConversations);

  if (!remindAt) {
    return {
      icon: 'inboxFilledSmall',
      text: <Plural id="banner-unsnooze" value={conversationCount} one="Unsnoozed" other="# unsnoozed" />,
    };
  }

  return {
    icon: 'snoozeFilledSmall',
    text: <Plural id="banner-snooze" value={conversationCount} one="Snoozed" other="# snoozed" />,
  };
}

function buildStageContent(conversationCount: number): UndoBannerContent {
  return {
    icon: 'bellCrossedSmall',
    text: (
      <Plural id="banner-unsubscribe" value={conversationCount} one="Unsubscribed" other="# unsubscribed" />
    ),
  };
}

function buildStatusContent(
  props: ConversationUndoBannerProps,
  conversation: ConversationBaseModel,
  conversationCount: number,
): UndoBannerContent {
  const {cellsContext} = props.state;
  const contextStatus = findConversationStatus(cellsContext, conversation);

  switch (contextStatus) {
    case ConversationStatusesEnum.ARCHIVED:
      return {
        icon: 'archiveFilledSmall',
        text: <Plural id="banner-archived" value={conversationCount} one="Archived" other="# archived" />,
      };

    case ConversationStatusesEnum.TRASHED:
      return {
        icon: 'trashFilledSmall',
        text: <Plural id="banner-trashed" value={conversationCount} one="Trashed" other="# trashed" />,
      };

    case ConversationStatusesEnum.SPAM:
      return {
        icon: 'spamFilledSmall',
        text: (
          <Plural id="banner-spam" value={conversationCount} one="Marked as spam" other="Marked # as spam" />
        ),
      };

    default:
      return {
        icon: 'inboxFilledSmall',
        text: <Plural id="banner-reopen" value={conversationCount} one="Reopened" other="# reopened" />,
      };
  }
}

function buildTagContent(
  tagProps: ConversationUndoBannerTagProps | undefined,
  conversationCount: number,
): UndoBannerContent | null {
  if (!tagProps) {
    return null;
  }

  if (tagProps.added.tag) {
    if (!tagProps.added.isStarredTag) {
      return {
        icon: 'moveRightFilledSmall',
        text: buildRegularTagText(tagProps.added.tag, conversationCount),
      };
    }

    return {
      icon: 'starFilledSmall',
      text: <Plural id="banner-starred" value={conversationCount} one="Starred" other="Starred #" />,
    };
  }

  if (tagProps.removed.tag && tagProps.removed.isStarredTag) {
    return {
      icon: 'starFilledSmall',
      text: <Plural id="banner-unstarred" value={conversationCount} one="Unstarred" other="Unstarred #" />,
    };
  }

  return null;
}

function buildRegularTagText(addedTag: TagModel, conversationCount: number) {
  if (!addedTag.isMainInboxTag) {
    return (
      <Plural
        id="banner-tag"
        value={conversationCount}
        one={`Moved to "${addedTag.name}"`}
        other={`Moved # to "${addedTag.name}"`}
      />
    );
  }

  return (
    <Plural id="banner-taginbox" value={conversationCount} one="Moved to Inbox" other="Moved # to Inbox" />
  );
}

function maybeRenderConversationSubject({state}: ConversationUndoBannerProps) {
  const {oldConversations, cellsContext, onlyOldConversation} = state;
  if (oldConversations.length !== 1) {
    return null;
  }

  if (onlyOldConversation) {
    return (
      <StyledSecondarySpan>
        <ConversationSubjectContainer
          conversation={onlyOldConversation}
          teammateId={cellsContext.teammateId}
        />
      </StyledSecondarySpan>
    );
  }

  // TODO: Introduce common ConversationBase subject logic and use it in CellMiddle and MobileCellMiddle.
  return (
    <StyledSecondarySpan>
      {oldConversations[0].subject || placeholderSubjectsByMessageType[oldConversations[0].messageType]}
    </StyledSecondarySpan>
  );
}
